/**
 * Small pieces the Lead Finder screens share: chips, the run status badge,
 * platform icons, and turning a map pick (ZoneMapPicker) into a search region.
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { LeadPlatform, LeadRegion, LeadRun, LeadRunStatus } from '../../services/api';
import type { TranslationKey } from '../../i18n/en';
import type { ZoneResult } from '../ZoneMapPicker';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export const PLATFORM_ICONS: Record<LeadPlatform, keyof typeof Ionicons.glyphMap> = {
  website: 'globe-outline',
  google: 'logo-google',
  facebook: 'logo-facebook',
  instagram: 'logo-instagram',
  linkedin: 'logo-linkedin',
  x: 'logo-twitter',
  tiktok: 'logo-tiktok',
  youtube: 'logo-youtube',
  snapchat: 'logo-snapchat',
  whatsapp: 'logo-whatsapp',
};

export const ALL_PLATFORMS = Object.keys(PLATFORM_ICONS) as LeadPlatform[];

export const statusColor = (status: LeadRunStatus): string => ({
  queued: colors.status.info,
  running: colors.primary,
  done: colors.status.success,
  partial: colors.status.warning,
  failed: colors.status.error,
  cancelled: colors.text.secondary,
}[status] as string);

/** The plan does not include Lead Finder (plan-features middleware) — or the subscription check said so. */
export const notInPlan = (res: any) =>
  res?.errors?.[0]?.code === 'FEATURE_NOT_IN_PLAN' || /not included in your subscription|plan does not include/i.test(res?.errors?.[0]?.message || res?.message || '');

/** What a running search is doing (the worker's phase). */
export const PHASE_KEYS: Record<string, TranslationKey> = {
  sources: 'leadFinder.phase.sources',
  links: 'leadFinder.phase.links',
  map: 'leadFinder.phase.map',
  google_maps: 'leadFinder.phase.google_maps',
  web: 'leadFinder.phase.web',
  enrich: 'leadFinder.phase.enrich',
  checking: 'leadFinder.phase.checking',
  scoring: 'leadFinder.phase.scoring',
};

/**
 * "Duplicate": the run whose settings the New search form opens with (set by the run screen,
 * read once by the Lead Finder screen).
 */
let pendingClone: LeadRun | null = null;
export const setPendingClone = (run: LeadRun | null) => { pendingClone = run; };
export const takePendingClone = (): LeadRun | null => { const run = pendingClone; pendingClone = null; return run; };

export const isActiveRun = (status: LeadRunStatus) => status === 'queued' || status === 'running';

/** Overture category ids ("coffee_shop") as words. */
export const categoryLabel = (id: string) => id.replace(/_/g, ' ');

/**
 * The map pick as a search region: a circle as is; a city / country outline as
 * a polygon (the picker gives [lat, lng] points, the API wants [lng, lat]);
 * without an outline, the city by name.
 */
export function regionFromZone(zone: ZoneResult): LeadRegion {
  if (zone.mode === 'radius') {
    return { type: 'circle', center: zone.center, radiusKm: Math.round(zone.radiusKm * 10) / 10, label: zone.name || zone.address };
  }
  if (zone.polygon && zone.polygon.length >= 3) {
    return { type: 'polygon', polygon: zone.polygon.map(([lat, lng]) => [lng, lat] as [number, number]), label: zone.name || zone.cityName };
  }
  return zone.mode === 'country'
    ? { type: 'area', country: zone.cityName, label: zone.name || zone.cityName }
    : { type: 'area', city: zone.cityName, label: zone.name || zone.cityName };
}

export function regionLabel(region?: LeadRegion | null): string | undefined {
  if (!region) return undefined;
  if (region.label) return region.label;
  if (region.type === 'circle') return `${region.radiusKm} km`;
  if (region.type === 'area') return [region.district, region.city, region.country].filter(Boolean).join(', ');
  return undefined;
}

export const Chip: React.FC<{
  label: string;
  selected?: boolean;
  onPress?: () => void;
  icon?: keyof typeof Ionicons.glyphMap;
  disabled?: boolean;
  onRemove?: () => void;
}> = ({ label, selected, onPress, icon, disabled, onRemove }) => {
  const chipStyles = useThemedStyles(getChipStyles);
  return (
  <TouchableOpacity
    style={[chipStyles.chip, selected && chipStyles.chipSelected, disabled && chipStyles.chipDisabled]}
    onPress={onPress}
    disabled={disabled || !onPress}
    activeOpacity={0.7}
  >
    {icon ? <Ionicons name={icon} size={14} color={(selected ? colors.text.onPrimary : colors.text.secondary) as string} /> : null}
    <Text style={[chipStyles.chipText, selected && chipStyles.chipTextSelected]} numberOfLines={1}>{label}</Text>
    {onRemove ? (
      <TouchableOpacity onPress={onRemove} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Ionicons name="close" size={14} color={(selected ? colors.text.onPrimary : colors.text.secondary) as string} />
      </TouchableOpacity>
    ) : null}
  </TouchableOpacity>
  );
};

export const StatusBadge: React.FC<{ label: string; color: string }> = ({ label, color }) => {
  const chipStyles = useThemedStyles(getChipStyles);
  return (
    <View style={[chipStyles.badge, { backgroundColor: color + '22' }]}>
      <Text style={[chipStyles.badgeText, { color }]}>{label}</Text>
    </View>
  );
};

/** A thin progress bar (0..1). */
export const ProgressBar: React.FC<{ value: number; color?: string }> = ({ value, color }) => {
  const chipStyles = useThemedStyles(getChipStyles);
  return (
    <View style={chipStyles.track}>
      <View style={[chipStyles.fill, { width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`, backgroundColor: color || (colors.primary as string) }]} />
    </View>
  );
};

const getChipStyles = () => StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  chipSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipDisabled: { opacity: 0.45 },
  chipText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  chipTextSelected: { color: colors.text.onPrimary, fontFamily: typography.fontFamily.semibold },
  badge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm, alignSelf: 'flex-start' },
  badgeText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold },
  track: { height: 6, borderRadius: 3, backgroundColor: colors.border, overflow: 'hidden' },
  fill: { height: 6, borderRadius: 3 },
});
